import React from 'react';
import { useTranslation } from 'react-i18next';
import { TrendingUp, Target, Users, Award, Briefcase, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';

const ExecutiveSearch = () => {
  const { t } = useTranslation();
  const executiveRoles = [
    { title: t('executiveSearch.rolesCTO'), icon: TrendingUp },
    { title: t('executiveSearch.rolesVP'), icon: Briefcase },
    { title: t('executiveSearch.rolesChiefSci'), icon: Award },
    { title: t('executiveSearch.rolesDirectorRD'), icon: Target },
    { title: t('executiveSearch.rolesHeadAI'), icon: Users },
    { title: t('executiveSearch.rolesVPHardware'), icon: TrendingUp }
  ];

  const searchProcess = [
    {
      step: '01',
      title: t('executiveSearch.process1Title'),
      description: t('executiveSearch.process1Desc')
    },
    {
      step: '02',
      title: t('executiveSearch.process2Title'),
      description: t('executiveSearch.process2Desc')
    },
    {
      step: '03',
      title: t('executiveSearch.process3Title'),
      description: t('executiveSearch.process3Desc')
    },
    {
      step: '04',
      title: t('executiveSearch.process4Title'),
      description: t('executiveSearch.process4Desc')
    }
  ];

  const scrollToContact = () => {
    document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="executive-search" className="py-24 bg-gradient-to-br from-white to-gray-50 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-900 to-slate-950 opacity-50" />
      <div className="absolute top-1/3 right-0 w-96 h-96 bg-cyan-50 rounded-full blur-3xl" />
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-cyan-50 to-teal-50 border border-cyan-200 rounded-full mb-6">
            <TrendingUp className="w-4 h-4 text-cyan-700" />
            <span className="text-sm font-medium text-cyan-600">{t('executiveSearch.title')} {t('executiveSearch.titleHighlight')}</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
            {t('executiveSearch.title')} <span className="bg-gradient-to-r from-cyan-400 to-teal-400 bg-clip-text text-transparent">{t('executiveSearch.titleHighlight')}</span>
          </h2>
          <p className="text-lg text-slate-700 leading-relaxed">
            {t('executiveSearch.subtitle')}
          </p>
        </div>

        {/* Combined Differentiator & Roles Section */}
        <div className="max-w-5xl mx-auto mb-16 p-8 lg:p-12 bg-gradient-to-br from-cyan-50 to-teal-50 border border-cyan-200 rounded-2xl backdrop-blur-sm relative overflow-hidden">
          {/* Background Image with Fade */}
          <div className="absolute inset-0 rounded-2xl overflow-hidden">
            <div 
              className="absolute inset-0 bg-cover bg-center"
              style={{ 
                backgroundImage: 'url(https://customer-assets.emergentagent.com/job_neon-engineers/artifacts/ff84542x_IMG_8704.JPG)',
                maskImage: 'linear-gradient(to bottom, transparent, black)',
                WebkitMaskImage: 'linear-gradient(to bottom, transparent, black)',
                opacity: 0.30,
                filter: 'grayscale(100%)'
              }}
            />
            {/* Fade overlay on upper third */}
            <div className="absolute inset-0 bg-gradient-to-b from-white via-white/40 to-transparent" style={{ height: '33.33%' }} />
          </div>
          
          {/* Content */}
          <div className="relative z-10">
            <h3 className="text-2xl font-bold text-slate-900 mb-4">{t('executiveSearch.diff1Title')}</h3>
            <p className="text-lg text-slate-700 leading-relaxed mb-8">
              {t('executiveSearch.description')}
            </p>
            
            {/* Leadership Roles */}
            <h4 className="text-xl font-bold text-slate-900 mb-6 mt-6">Leadership Roles We Place</h4>
            <div className="grid md:grid-cols-3 gap-4">
              {executiveRoles.map((role, index) => {
                const Icon = role.icon;
                return (
                  <div 
                    key={index}
                    className="group flex items-center space-x-3 p-4 bg-white/80 backdrop-blur-sm border-2 border-gray-200 rounded-xl hover:border-cyan-600 hover:bg-white/90 transition-all duration-300"
                  >
                    <div className="w-10 h-10 bg-gradient-to-br from-cyan-600 to-teal-600 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-slate-700 font-medium group-hover:text-cyan-600 transition-colors">
                      {role.title}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Search Process */}
        <div className="max-w-6xl mx-auto mb-16">
          <h3 className="text-2xl font-bold text-slate-900 text-center mb-12">{t('executiveSearch.processTitle')}</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {searchProcess.map((process, index) => (
              <div 
                key={index}
                className="relative p-6 bg-gray-50/50 border-2 border-gray-200 rounded-xl hover:border-cyan-600 transition-all duration-300 group"
              >
                {/* Step Number */}
                <div className="text-6xl font-bold text-cyan-900/30 group-hover:text-cyan-800/50 transition-colors absolute top-4 right-4">
                  {process.step}
                </div>
                
                <div className="relative z-10">
                  <h4 className="text-lg font-bold text-slate-900 mb-3 group-hover:text-cyan-600 transition-colors">
                    {process.title}
                  </h4>
                  <p className="text-sm text-slate-700 leading-relaxed">
                    {process.description}
                  </p>
                </div>

                {/* Connector Line for desktop */}
                {index < searchProcess.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-3 w-6 h-0.5 bg-gradient-to-r from-cyan-500/50 to-transparent" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="max-w-4xl mx-auto text-center">
          <div className="p-8 lg:p-12 bg-gradient-to-br from-white to-gray-50 border border-cyan-200 rounded-2xl backdrop-blur-sm">
            <h3 className="text-2xl lg:text-3xl font-bold text-slate-900 mb-4">
              {t('executiveSearch.ctaTitle')}
            </h3>
            <p className="text-lg text-slate-700 mb-8 leading-relaxed">
              {t('executiveSearch.ctaDesc')}
            </p>
            <Button 
              onClick={scrollToContact}
              size="lg"
              className="group bg-gradient-to-r from-cyan-600 to-teal-600 hover:from-cyan-500 hover:to-teal-500 text-slate-900 font-bold px-8 py-6 text-lg rounded-xl shadow-xl shadow-cyan-500/30 hover:shadow-cyan-500/50 transition-all duration-300"
            >
              {t('executiveSearch.ctaButton')}
              <ChevronRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ExecutiveSearch;
